import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  arrayUnion,
  arrayRemove,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION_NAME = 'sections';

export const firebaseSectionService = {
  // Obtener todas las secciones
  async getAllSections() {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error al obtener secciones:', error);
      throw new Error('Error al obtener las secciones');
    }
  },

  // Obtener secciones activas
  async getActiveSections() {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('isActive', '==', true),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error al obtener secciones activas:', error);
      throw new Error('Error al obtener las secciones activas');
    }
  },

  // Obtener sección por ID
  async getSectionById(id) {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Sección no encontrada');
      }
    } catch (error) {
      console.error('Error al obtener sección:', error);
      throw new Error('Error al obtener la sección');
    }
  },

  // Crear nueva sección
  async createSection(sectionData) {
    try {
      // Obtener el orden más alto actual
      const existingSections = await this.getAllSections();
      const maxOrder = existingSections.length > 0 
        ? Math.max(...existingSections.map(s => s.order || 0))
        : 0;
      
      // Asegurar que wineIds siempre es un array
      const safeSectionData = {
        ...sectionData,
        wineIds: Array.isArray(sectionData.wineIds) ? sectionData.wineIds : [],
      };
      
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...safeSectionData,
        order: maxOrder + 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return {
        id: docRef.id,
        ...safeSectionData,
        order: maxOrder + 1
      };
    } catch (error) {
      console.error('Error al crear sección:', error);
      throw new Error('Error al crear la sección');
    }
  },

  // Actualizar sección
  async updateSection(id, sectionData) {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, {
        ...sectionData,
        updatedAt: serverTimestamp()
      });
      
      return {
        id,
        ...sectionData
      };
    } catch (error) {
      console.error('Error al actualizar sección:', error);
      throw new Error('Error al actualizar la sección');
    }
  },

  // Eliminar sección
  async deleteSection(id) {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await deleteDoc(docRef);
      return { success: true };
    } catch (error) {
      console.error('Error al eliminar sección:', error);
      throw new Error('Error al eliminar la sección');
    }
  },

  // Añadir vino a sección
  async addWineToSection(sectionId, wineId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, sectionId);
      await updateDoc(docRef, {
        wineIds: arrayUnion(wineId),
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error al añadir vino a sección:', error);
      throw new Error('Error al añadir el vino a la sección');
    }
  },

  // Eliminar vino de sección
  async removeWineFromSection(sectionId, wineId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, sectionId);
      await updateDoc(docRef, {
        wineIds: arrayRemove(wineId),
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error al eliminar vino de sección:', error);
      throw new Error('Error al eliminar el vino de la sección');
    }
  },

  // Obtener secciones por tipo
  async getSectionsByType(type) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('type', '==', type),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error al obtener secciones por tipo:', error);
      throw new Error('Error al obtener las secciones por tipo');
    }
  },

  // Obtener secciones con vinos incluidos
  async getSectionsWithWines() {
    try {
      const sections = await this.getActiveSections();
      
      // Aquí podrías hacer una consulta adicional para obtener los datos de los vinos
      // Por ahora retornamos las secciones con los IDs de vinos
      return sections;
    } catch (error) {
      console.error('Error al obtener secciones con vinos:', error);
      throw new Error('Error al obtener las secciones con vinos');
    }
  },

  // Mover sección hacia arriba
  async moveSectionUp(sectionId) {
    try {
      const sections = await this.getAllSections();
      const currentIndex = sections.findIndex(s => s.id === sectionId);
      
      if (currentIndex <= 0) {
        throw new Error('La sección ya está en la primera posición');
      }
      
      const currentSection = sections[currentIndex];
      const previousSection = sections[currentIndex - 1];
      
      // Intercambiar órdenes
      await updateDoc(doc(db, COLLECTION_NAME, currentSection.id), {
        order: previousSection.order,
        updatedAt: serverTimestamp()
      });
      
      await updateDoc(doc(db, COLLECTION_NAME, previousSection.id), {
        order: currentSection.order,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error al mover sección hacia arriba:', error);
      throw new Error('Error al mover la sección');
    }
  },

  // Mover sección hacia abajo
  async moveSectionDown(sectionId) {
    try {
      const sections = await this.getAllSections();
      const currentIndex = sections.findIndex(s => s.id === sectionId);
      
      if (currentIndex === -1 || currentIndex >= sections.length - 1) {
        throw new Error('La sección ya está en la última posición');
      }
      
      const currentSection = sections[currentIndex];
      const nextSection = sections[currentIndex + 1];
      
      // Intercambiar órdenes
      await updateDoc(doc(db, COLLECTION_NAME, currentSection.id), {
        order: nextSection.order,
        updatedAt: serverTimestamp()
      });
      
      await updateDoc(doc(db, COLLECTION_NAME, nextSection.id), {
        order: currentSection.order,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error al mover sección hacia abajo:', error);
      throw new Error('Error al mover la sección');
    }
  }
};

// Función para inicializar datos de ejemplo
export const initializeSectionData = async () => {
  try {
    const sections = [
      {
        name: 'Los Mejores de Marqués de Riscal',
        description: 'Selección premium de la prestigiosa bodega riojana',
        type: 'winery',
        maxWines: 4,
        isActive: true,
        wineIds: []
      },
      {
        name: 'Nuestras Recomendaciones',
        description: 'Vinos seleccionados por nuestro equipo de expertos',
        type: 'featured',
        maxWines: 6,
        isActive: true,
        wineIds: []
      },
      {
        name: 'Vinos de Temporada',
        description: 'Los vinos perfectos para cada estación del año',
        type: 'seasonal',
        maxWines: 8,
        isActive: true,
        wineIds: []
      }
    ];

    for (const section of sections) {
      await firebaseSectionService.createSection(section);
    }

    console.log('✅ Datos de secciones inicializados correctamente');
  } catch (error) {
    console.error('❌ Error al inicializar datos de secciones:', error);
  }
};

// Exportar para uso en desarrollo
if (process.env.NODE_ENV === 'development') {
  window.firebaseSectionService = firebaseSectionService;
  window.initializeSectionData = initializeSectionData;
} 